import type { PricingPlan } from "@/components/Plans/Plans";
import { BillingCycleType, CurrencyConstants, IntervalUnit } from "@/constants";
import { usePublicAPI } from "@/services/apiService";
import { ProductService } from "@/services/productService";
import type { Product, ProductPrice } from "@/types/product";
import useSWR from "swr";

const PRODUCTS_KEY = "/products?include_prices=true";

const deriveBillingCycle = ({ period_interval, period_count }: ProductPrice): BillingCycleType => {
  if (period_interval === IntervalUnit.MONTH && period_count === 1) return BillingCycleType.MONTHLY;
  if (period_interval === IntervalUnit.YEAR && period_count === 1) return BillingCycleType.YEARLY;
  return BillingCycleType.ONE_TIME;
};

export const transformProductsToPricingPlans = (products: Product[]): PricingPlan[] => {
  const plans: PricingPlan[] = [];

  for (const product of products) {
    for (const price of product.prices) {
      if (!price.is_active) continue;

      const [baseTier] = price.price_tiers;
      if (!baseTier) {
        console.warn("Skipping price with no tiers", { priceId: price.id });
        continue;
      }
      const ratedTier = price.price_tiers.find((tier) => tier.amount_per_credit > 0);

      plans.push({
        pricingId: price.id,
        name: product.name,
        description: product.description || "",
        tier_order: product.tier_order,
        price: baseTier.fixed_amount / CurrencyConstants.MINOR_UNITS_PER_CURRENCY_UNIT,
        currency: price.currency,
        billingCycle: deriveBillingCycle(price),
        pages: baseTier.amount_per_credit === 0 ? (baseTier.credit_limit ?? 0) : 0,
        overusagePricing: ratedTier
          ? ratedTier.amount_per_credit / CurrencyConstants.MINOR_UNITS_PER_CURRENCY_UNIT
          : undefined,
        isPopular: price.metadata?.recommended ?? false,
        features: product.metadata?.features || [],
      });
    }
  }

  // Sort by tier
  plans.sort((a, b) => b.tier_order - a.tier_order);

  return plans;
};

export const useProducts = () => {
  const publicApi = usePublicAPI("v2");
  const productService = new ProductService(publicApi);

  const { data, error, isLoading } = useSWR<Product[]>(
    PRODUCTS_KEY,
    () => productService.fetchProducts(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 1800000, // 30 mins
    }
  );

  return {
    products: data,
    isLoading,
    isError: error,
  };
};

export const usePricingPlans = () => {
  const { products, isLoading, isError } = useProducts();

  const { data: plans } = useSWR<PricingPlan[]>(
    products ? ["pricing-plans", products] : null,
    () => {
      if (!products) return [];
      return transformProductsToPricingPlans(products);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    plans: plans || [],
    isLoading,
    isError,
  };
};
